import { motion } from "framer-motion";
import { ShieldCheck, Satellite, Headphones, CreditCard } from "lucide-react";

const IMG =
  "https://images.unsplash.com/photo-1624397640148-949b1732bb0a?auto=format&fit=crop&w=1200&q=80";

const ITEMS = [
  {
    icon: ShieldCheck,
    title: "German-grade structures",
    text: "Hot-dip galvanized mounting steel, engineered for cyclone-rated winds and 30 years of monsoons.",
  },
  {
    icon: Satellite,
    title: "AI cloud monitoring",
    text: "Track every unit your roof generates from a sleek app — alerts before you even notice a dip.",
  },
  {
    icon: Headphones,
    title: "24/7 grid support",
    text: "Net-metering paperwork, DISCOM liaison and rapid service crews — we handle the bureaucracy.",
  },
  {
    icon: CreditCard,
    title: "0% interest EMI",
    text: "Start saving from day one with flexible financing that often costs less than your current bill.",
  },
];

export const WhyUs = () => (
  <section id="why" className="relative py-24 lg:py-32">
    <div className="mx-auto max-w-[1400px] px-5 sm:px-8">
      <div className="grid items-center gap-14 lg:grid-cols-12">
        <motion.div
          initial={{ opacity: 0, scale: 0.96 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
          className="relative lg:col-span-5"
        >
          <div className="absolute -inset-4 rounded-[2rem] bg-gold/10 blur-2xl" aria-hidden="true" />
          <div className="relative overflow-hidden rounded-[1.6rem] border border-gold/20">
            <img
              src={IMG}
              alt="Certified engineer installing a solar mounting system"
              loading="lazy"
              className="h-[380px] sm:h-[520px] w-full object-cover"
              data-testid="why-us-image"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-ink via-transparent to-transparent" />
            <div className="glass absolute bottom-5 left-5 right-5 rounded-2xl border border-white/10 p-5">
              <p className="font-display text-2xl font-bold text-gold-grad">In-house crews only</p>
              <p className="mt-1 text-xs text-mutegray">
                No subcontractors. Every installation is done by Maa Sakumbri certified engineers.
              </p>
            </div>
          </div>
        </motion.div>

        <div className="lg:col-span-7">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
          >
            <p className="eyebrow">04 — Why Maa Sakumbri</p>
            <h2 className="mt-4 font-display text-3xl sm:text-5xl font-bold tracking-tight leading-[1.08]">
              Built like infrastructure.
              <br />
              <span className="text-gold-grad">Cared for like family.</span>
            </h2>
          </motion.div>

          <div className="mt-10 grid gap-4 sm:grid-cols-2">
            {ITEMS.map((item, i) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.6, delay: i * 0.08, ease: [0.16, 1, 0.3, 1] }}
                className="group rounded-2xl border border-white/10 bg-surface p-6 transition-all duration-300 hover:border-gold/35 hover:bg-surface-2"
                data-testid={`why-us-card-${i}`}
              >
                <item.icon className="h-7 w-7 text-gold transition-transform duration-300 group-hover:scale-110" />
                <h3 className="mt-4 font-display text-lg font-bold text-warm">{item.title}</h3>
                <p className="mt-2 text-[13px] leading-relaxed text-mutegray">{item.text}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </div>
  </section>
);

export default WhyUs;
